'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowUpRight } from 'lucide-react';
import { Category } from '@/data/categories';

interface CategoryCardProps {
  category: Category;
  index: number;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ category, index }) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const quickLift = useRef<gsap.QuickToFunc | null>(null);
  const quickRotX = useRef<gsap.QuickToFunc | null>(null);
  const quickRotY = useRef<gsap.QuickToFunc | null>(null);

  const number = String(index + 1).padStart(2, '0');

  useGSAP(() => {
    if (!cardRef.current) return;
    gsap.set(cardRef.current, { transformPerspective: 800, transformOrigin: 'center' });
    quickLift.current = gsap.quickTo(cardRef.current, 'y', { duration: 0.5, ease: 'power3.out' });
    quickRotX.current = gsap.quickTo(cardRef.current, 'rotationX', { duration: 0.6, ease: 'power3.out' });
    quickRotY.current = gsap.quickTo(cardRef.current, 'rotationY', { duration: 0.6, ease: 'power3.out' });
  }, { scope: cardRef });

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;

    quickRotY.current?.(px * 8);
    quickRotX.current?.(-py * 8);
    quickLift.current?.(-6);
  };

  const handleMouseLeave = () => {
    quickRotY.current?.(0);
    quickRotX.current?.(0);
    quickLift.current?.(0);
  };

  return (
    <Link
      ref={cardRef}
      href={category.href || '#'}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="group relative block bg-[#FAF8F4] rounded-[6px] overflow-hidden border border-[#1C1B19]/[0.07] shadow-[0_1px_3px_rgba(28,27,25,0.05)] hover:shadow-[0_30px_60px_-15px_rgba(28,27,25,0.25)] transition-shadow duration-500 will-change-transform"
    >
      {/* Image */}
      <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#F1EEE7]">
        <Image
          src={category.image}
          alt={category.title}
          fill
          sizes="(max-width: 375px) 100vw, (max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          className="object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.12]"
        />

        {/* tone-down veil, lifts on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent transition-opacity duration-500 group-hover:opacity-40" />

        {/* diagonal foil ribbon */}
        {/* <div className="absolute -left-9 top-5 -rotate-45 w-32 bg-[#B08D57] py-1 text-center shadow-md">
          <span className="text-[10px] tracking-[0.2em] text-[#FAF8F4] font-semibold">{number}</span>
        </div> */}

        {/* hover reveal panel */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] bg-white/10 backdrop-blur-md border-t border-white/20 px-4 py-3 flex items-center justify-between">
          <span className="text-[11px] tracking-[0.15em] uppercase text-white font-semibold">Explore</span>
          <ArrowUpRight size={16} className="text-white" strokeWidth={2} />
        </div>
      </div>

      {/* Label */}
      <div className="relative bg-[#FAF8F4] py-4 px-4 text-center">
        <span className="block text-[10px] tracking-[0.18em] uppercase text-[#9A9384] font-medium mb-1">
          {category.subtitle}
        </span>
        <h3 className="text-[16px] xs:text-[17px] font-bold text-[#1C1B19] tracking-tight group-hover:text-[#8C6D3F] transition-colors duration-300">
          {category.title}
        </h3>
        <span className="relative mt-2 mx-auto block h-px w-6 bg-[#1C1B19]/15 overflow-hidden">
          <span className="absolute inset-y-0 left-0 w-0 bg-[#B08D57] transition-all duration-500 ease-out group-hover:w-full" />
        </span>
      </div>
    </Link>
  );
};